import React, { useCallback, useEffect, useState } from 'react'
import { Box, Skeleton, Stack, useTheme } from '@mui/material'
import { Paper } from '@e3dc-react/shell/Elements/Paper'
import { Formik } from 'formik'
import FormBox from '../../form/components/FormBox'
import CompanyProfileFormFields from 'src/Libs/company-settings/components/CompanyProfileFormFields'
import { useCompanyProfile } from 'src/Libs/company-settings/hooks'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { companyModelToCompanyProfileForm } from 'src/Libs/company-settings/transformer'
import { CompanyProfileFormModel } from 'src/Libs/company-settings/models'
import { useUser } from 'src/Hooks/useUser'
import { isUndefined } from 'lodash'
import CompanyShowRooms from './showrooms/CompanyShowRooms'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import useModals from 'src/Hooks/useModals'
import { useCountryOptions, useCountryCodesValidation } from '@e3dc-react/shell/Hooks/useCountryOptions'
import { ContactData } from 'src/Libs/user/models'
import { useShopCountryAccess } from 'src/Libs/shop/hooks/use-shop-country-access'

const CompanyAddressEditor: React.FC = () => {
  const [countryCodeValidate] = useCountryCodesValidation()
  const { userCompany: company } = useUser()
  const { updateCompanyProfile } = useCompanyProfile()
  const { snackError } = useSnackbar()
  const [processing, setProcessing] = useState<boolean>(false)
  const [initialValues, setInitialValues] = useState<CompanyProfileFormModel | undefined>()
  const { t } = useCustomTranslation()
  const { palette } = useTheme()
  const { openConfirmationModal } = useModals()
  const { allCountries } = useCountryOptions()
  const { checkIfCompanyHasShopAccess } = useShopCountryAccess()

  const openConfirmCountryChangeModal = (companyId: number, values: CompanyProfileFormModel): void => {
    const country = allCountries.find(option => option.value === values.country)?.label
    openConfirmationModal({
      title: t('companyEditor.confirmLocationTitle', { country }),
      buttonText: [t('common.cancel'), t('common.confirm')],
      text: t('companyEditor.confirmLocationText', { country }),
      onConfirm: closeDialog => {
        performUpdate(companyId, values)
        closeDialog()
      },
    })
  }
  const performUpdate = (companyId: number, values: CompanyProfileFormModel): void => {
    setProcessing(true)
    updateCompanyProfile(companyId, values)
      .catch(snackError)
      .finally(() => setProcessing(false))
  }

  useEffect(() => {
    if (!company) {
      return
    }

    const country = company.contactData?.Land ?? ''

    setInitialValues(
      companyModelToCompanyProfileForm({
        ...company,
        contactData: {
          ...(company.contactData as ContactData),
          Land: countryCodeValidate(country) ? country : '',
        },
      })
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company])

  const handleSubmit = useCallback(
    (values: CompanyProfileFormModel): void => {
      if (!company) {
        return
      }
      if (!checkIfCompanyHasShopAccess(values.country)) {
        openConfirmCountryChangeModal(company.id, values)
      } else {
        performUpdate(company.id, values)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [company && company.id]
  )

  if (isUndefined(company) || isUndefined(initialValues)) {
    return <Skeleton />
  }

  return (
    <Stack spacing={2}>
      <Paper title={company.Name}>
        <Box sx={{ p: 4, pb: 2 }}>
          <Formik initialValues={initialValues} onSubmit={handleSubmit} enableReinitialize>
            <FormBox model={initialValues} separator separatorColor={palette.grey.grey03} isLoading={processing}>
              <CompanyProfileFormFields companyId={company.id} companyName={company.Name ?? ''} partnerNumber={company.partnerNumber} />
            </FormBox>
          </Formik>
        </Box>
      </Paper>
      <Paper title={t('administration.companyManagement.showroom.title')}>
        <Box sx={{ p: 4, pb: 2 }}>
          <CompanyShowRooms companyId={company.id} />
        </Box>
      </Paper>
    </Stack>
  )
}

export default CompanyAddressEditor
